import React from 'react'

import { useQuery, useMutation } from 'react-apollo'
import gql from 'graphql-tag'
import _ from 'lodash/fp'

import colors from '@fe/ui/colors'
import { Progress } from '@fe/ui/icons'

import ApolloClient from '../../apollo'
import Button from '../../components/Button'
import { BaseBuddy } from './Buddy'

import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import Slide from '@material-ui/core/Slide'
import Snackbar from '@material-ui/core/Snackbar'
import { People, GolfCourse, Add } from '@material-ui/icons'
import { Tooltip } from '@material-ui/core'

const TransitionRight = (props) => {
  return (
    <Slide {...props} direction='right' />
  )
}

const buddySuggestionsQuery = gql`
query buddySuggestions($pagination: Pagination) {
  buddySuggestions(pagination: $pagination) {
    id
    handicap
    commonGames
    commonBuddies
    homeClubName
    homeClubId
    name
    orgIds
  }
}
`

const addBuddyMutation = gql`
mutation sendBuddyRequest($to: String!) { # $to is userId (UUID)
  sendBuddyRequest(to: $to)
}
`

const SuggestionBasis = ({ suggestion }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginLeft: 4, marginRight: 4 }}>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
        <GolfCourse style={{ fontSize: 16, color: '#00000080' }} />
        <Typography style={{ color: '#00000080' }}>
          {_.get('commonGames', suggestion)}
        </Typography>
      </div>

      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
        <People style={{ fontSize: 16, color: '#00000080' }} />
        <Typography style={{ color: '#00000080' }}>
          {_.get('commonBuddies', suggestion)}
        </Typography>
      </div>
    </div>
  )
}

const Suggest = ({ title, ...props }) => {
  const [amount, setAmount] = React.useState(4)
  const [attempts, setAttempts] = React.useState([])
  const data = useQuery(buddySuggestionsQuery, { variables: { pagination: { offset: 0, limit: amount } }, client: ApolloClient })
  const [addBuddy, addBuddyResponse] = useMutation(addBuddyMutation, { client: ApolloClient })
  const [snackTransition, setSnackTransition] = React.useState()
  const [snackOpen, setSnackOpen] = React.useState()

  React.useEffect(() => {
    const loading = _.get('loading', data)
    const called = _.get('called', data)
    const suggestions = _.flow(
      _.get('data.buddySuggestions'),
      _.size
    )(data)
    if (!loading && called && (suggestions === 0)) {
      props.skip()
    }
  }, [props.skip, data])

  const showSnackbar = React.useCallback((Transition) => {
    setSnackTransition(() => Transition)
    setSnackOpen(true)
  }, [setSnackTransition, setSnackOpen])

  const handleClose = React.useCallback(() => {
    setSnackOpen(false)
  }, [setSnackOpen])

  const handleSuggestionClick = React.useCallback((suggestion) => async () => {
    const id = _.get('id', suggestion)
    setAttempts(_.union(attempts, [id]))
    try {
      await addBuddy({ variables: { to: id } })
    } catch (e) {
      console.warn(e)
      setAttempts(_.without([id], attempts))
    }
    showSnackbar(TransitionRight)
  }, [data, setAttempts, attempts])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <h1 style={{ fontSize: '2em', textTransform: 'uppercase', fontWeight: 'bold' }}>
        More Fun with Buddies
      </h1>
      {_.map((suggestion) => {
        const id = _.get('id', suggestion)
        const tried = _.includes(id, attempts)
        const loading = _.get('loading', addBuddyResponse) && (_.first(attempts) === id)
        return (
          <div key={`${id}_suggest`} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Snackbar
              open={snackOpen}
              onClose={handleClose}
              TransitionComponent={snackTransition}
              autoHideDuration={1500}
              message={_.getOr('Request Sent!', 'error.message', addBuddyResponse)}
              key={`${id}_sent`}
            />

            <BaseBuddy
              profile={suggestion}
            />
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <SuggestionBasis suggestion={suggestion} />
              <Tooltip
                disableTouchListener
                disableHoverListener={!tried || loading}
                id={`${id}_tooltip`}
                title={(
                  <Typography>
                    Request already sent
                  </Typography>
                )}
              >
                <span>
                  {/* The span is for the tooltip to work on a disabled button */}
                  <Button disabled={tried} onClick={handleSuggestionClick(suggestion)}>
                    {tried
                      ? loading
                          ? <CircularProgress style={{ color: colors.lightGreen }} size={22} />
                          : (
                            <Progress style={{ color: colors.lightOrange }} />
                            )
                      : <Add />}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
        )
      }, _.get('data.buddySuggestions', data))}
      <Button
        loading={_.get('loading', data)}
        loaderRight={<CircularProgress style={{ color: colors.lightGreen }} size={22} />}
        onClick={() => setAmount(amount + 4)}
      >
        Load More
      </Button>
    </div>
  )
}

export default Suggest
